<template>
  <v-dialog
    hide-overlay
    :overlay-opacity="0"
    transition="fade-transition"
    persistent
    ref="dialog"
    v-model="isOpen"
    absolute
  >
    <div class="card-overlay" @click="$refs.dialog.animateClick()"/>

    <v-card style="width: 560px" v-if="isOpen">
      <v-card-text height="800px" class="px-8 pt-8">
        <div class="mb-8 d-flex align-start">
          <div class="d-flex flex-column">
            <span class="font-weight-light p-1 base--text">
              {{ $t("heading.onboarding.gitDeploy.title") }}
            </span>
            <h4 class>{{ $t("heading.onboarding.gitDeploy.subtitle") }}</h4>
          </div>

          <v-btn
            x-small
            icon
            :disabled="loading || connectionTestLoading"
            @click="$emit('remove-modal')"
            class="ml-auto"
          >
            <v-icon size="24" color="gray">$close</v-icon>
          </v-btn>
        </div>

        <v-divider class="mb-5"/>

        <div class="form-box">
          <div>
            <span
              class="gray--text text--darken-1 mb-1"
              :class="errorMessage ? 'error--text' : ''"
            >
              {{ $t("heading.onboarding.gitDeploy.repoUrl.label") }}
            </span>
            <v-text-field
              v-model="repoUrl"
              outlined
              dense
              ref="input"
              :error="!!errorMessage"
              class="mt-2 v-input--md"
              placeholder="https://"
              @keydown.enter.prevent="submit"
              autofocus
            />
          </div>

          <div class="mt-4 d-flex align-center justify-space-between">
            <span class="font-weight-medium gray--text text--darken-1">
              {{ $t("heading.onboarding.gitDeploy.privateRepo.label") }}
            </span>
            <v-switch v-model="isPrivate" inset hide-details class="ma-0"/>
          </div>

          <v-expand-transition>
            <div v-if="isPrivate" class="mt-4">
              <span class="gray--text text--darken-1 mb-1 d-block">
                {{ $t("heading.onboarding.gitDeploy.auth.pat.tokenLabel") }}
              </span>
              <v-text-field
                v-model="patToken"
                outlined
                dense
                hide-details
                class="mt-2 mb-0 v-input--md"
                :type="showToken ? 'text' : 'password'"
                :placeholder="$t('heading.onboarding.gitDeploy.auth.pat.placeholder')"
                @keydown.enter.prevent="submit"
              >
                <template v-slot:append>
                  <v-btn x-small icon @click="showToken = !showToken">
                    <v-icon size="18" color="gray">{{
                        showToken ? "$eyeclosed" : "$eye"
                      }}
                    </v-icon>
                  </v-btn>
                </template>
              </v-text-field>
              <form-field-doc-hint
                class="mt-1"
                :link-text="$t('message.link.documentation.howToCreate')"
              />
            </div>
          </v-expand-transition>
        </div>

        <v-scale-transition>
          <div class="error-box mt-5" v-if="errorMessage && !connectionTestLoading">
            <v-icon color="error" size="20" class="mr-2">$alertcircle</v-icon>
            <span class="error--text">
              <b class="error--text">
                <i18next :translation="$t('message.alert.failed')">
                  <template #message>
                    <span class="font-weight-medium">{{ errorMessage }}</span>
                  </template>
                </i18next>
              </b>
            </span>
          </div>
        </v-scale-transition>
      </v-card-text>

      <v-card-actions class="pt-8 d-flex align-center justify-space-between">
        <div>
          <StepIndicator :currentStep="2" :maxSteps="4"/>
        </div>
        <div class="d-flex align-center">
          <v-btn
            large
            :disabled="loading || connectionTestLoading"
            @click="$emit('remove-modal')"
            text
            elevation="0"
            class="mr-3"
          >
            <span class="font-weight-bold gray--text text--darken-1">
              {{ $t("button.cancel") }}
            </span>
          </v-btn>
          <v-btn
            large
            @click="submit"
            :disabled="isContinueDisabled"
            color="primary"
            elevation="0"
            class="primary"
            :loading="loading || connectionTestLoading"
          >
            <span class="font-weight-bold">
              {{ $t("button.continue") }}
              <v-icon size="20" class="mr-0 ml-3">$arrowright</v-icon>
            </span>
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Api from "@/apis/Api";
import FormFieldDocHint from "@/components/FormFieldDocHint.vue";
import StepIndicator from "../StepIndicator.vue";

export default {
  name: "OnboardingDeployFromGitModal",
  components: { StepIndicator, FormFieldDocHint },
  props: {
    isOpen: {type: Boolean, default: false},
    loading: {type: Boolean, default: false},
  },
  data: () => ({
    repoUrl: "",
    isPrivate: false,
    patToken: "",
    showToken: false,
    errorMessage: null,
    connectionTestLoading: false,
  }),
  computed: {
    normalizedRepoUrl() {
      return (this.repoUrl || "").trim();
    },
    isContinueDisabled() {
      if (!this.normalizedRepoUrl) return true;
      if (this.isPrivate && !(this.patToken || "").trim()) return true;
      return false;
    },
  },
  methods: {
    resetData() {
      this.repoUrl = "";
      this.isPrivate = false;
      this.patToken = "";
      this.showToken = false;
      this.errorMessage = null;
      this.connectionTestLoading = false;
    },
    runGitConnectionTest() {
      const token = (this.patToken || "").trim();

      return Api.post("/instances/test-git-url", {
        repo_url: this.normalizedRepoUrl,
        token: this.isPrivate && token ? token : null,
      })
        .then((response) => response.data)
        .catch((error) => Promise.reject(Api.getErrorMessage(error)));
    },
    async submit() {
      if (
        this.isContinueDisabled ||
        this.loading ||
        this.connectionTestLoading
      ) {
        return;
      }

      this.errorMessage = null;
      this.connectionTestLoading = true;

      try {
        const result = await this.runGitConnectionTest();

        if (result.success === true) {
          const token = (this.patToken || "").trim();
          this.$emit("next", {
            repo_url: this.normalizedRepoUrl,
            token: this.isPrivate && token ? token : null,
          });
          return;
        }

        this.errorMessage =
          result.error_message || this.$t("notification.connectionFailed");
      } catch (error) {
        this.errorMessage = error;
      } finally {
        this.connectionTestLoading = false;
      }
    },
  },
  watch: {
    isOpen: function (value) {
      this.resetData();
      if (value) {
        this.$nextTick(() => {
          this.$store.dispatch("lockBodyScroll");
        });
      } else {
        this.$store.dispatch("unlockBodyScroll");
      }
    },
    isPrivate: function (value) {
      if (!value) {
        this.errorMessage = null;
      }
    },
    repoUrl: function () {
      this.errorMessage = null;
    },
    patToken: function () {
      this.errorMessage = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.v-dialog__content::v-deep {
  .v-dialog {
    overflow-y: auto;
    overflow-x: hidden;
  }
}

.v-card::v-deep {
  .v-card__text {
    -webkit-line-clamp: unset !important;
  }

  .v-text-field--outlined > .v-input__control > .v-input__slot,
  .v-textarea--outlined > .v-input__control > .v-input__slot {
    background-color: #fff;
  }

  .form-box {
    border: 1px solid var(--v-gray-lighten4);
    border-radius: 8px;
    padding: 16px;
  }

  .error-box {
    background-color: var(--v-error-lighten5);
    border-radius: 8px;
    padding: 16px;
    display: flex;
  }

  @media (max-width: 480px) {
    .v-card__text,
    .v-card__actions {
      padding-left: 24px !important;
      padding-right: 24px !important;
    }
  }
}
</style>
